import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { swipeAPI } from '../services/api';
import { SwipeCard } from '../components/SwipeCard';
import { MatchModal } from '../components/MatchModal';
import { BottomNav } from '../components/BottomNav';
import DiscoveryFiltersModal from '../components/DiscoveryFiltersModal';
import { Sliders, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function DiscoverPage() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showMatch, setShowMatch] = useState(false);
  const [matchData, setMatchData] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [settings, setSettings] = useState(null);
  const [filtersApplied, setFiltersApplied] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const response = await swipeAPI.getSettings();
      setSettings(response.data.settings);
      
      // Check if any filters are applied
      const s = response.data.settings;
      const hasFilters = s && (
        s.age_min !== 18 || 
        s.age_max !== 60 || 
        s.distance_type !== 'worldwide' ||
        (s.preferred_countries && s.preferred_countries.length > 0) ||
        (s.preferred_intents && s.preferred_intents.length > 0) ||
        (s.preferred_religions && s.preferred_religions.length > 0)
      );
      setFiltersApplied(hasFilters);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  }, []);

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await swipeAPI.discover();
      const newProfiles = response.data.profiles || [];
      setProfiles(newProfiles);
      setCurrentIndex(0);
      if (newProfiles.length > 0) {
        toast.success(`Found ${newProfiles.length} new profiles!`);
      } else {
        toast.info('No new profiles found. Try adjusting your filters.');
      }
    } catch (error) {
      console.error('Failed to load profiles:', error);
      toast.error('Failed to load profiles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadProfiles();
  }, [loadSettings, loadProfiles]);

  const handleSwipe = async (action) => {
    const currentProfile = profiles[currentIndex];
    if (!currentProfile) return;

    try {
      const response = await swipeAPI.action({
        target_user_id: currentProfile.user_id,
        action: action
      });

      if (response.data.is_match) {
        setMatchData(response.data.match);
        setShowMatch(true);
      }

      setCurrentIndex(prev => prev + 1);

      // Load more if running low
      if (currentIndex >= profiles.length - 3) {
        loadProfiles();
      }
    } catch (error) {
      console.error('Swipe action failed:', error);
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail || 'Daily swipe limit reached. Upgrade to Premium for unlimited swipes!');
      } else {
        toast.error('Swipe failed. Please try again.');
      }
    }
  };

  const handleSaveFilters = async (filters) => {
    try {
      await swipeAPI.updateSettings(filters);
      setSettings(filters);
      
      // Check if any filters are applied
      const hasFilters = (
        filters.age_min !== 18 || 
        filters.age_max !== 60 || 
        filters.distance_type !== 'worldwide' ||
        (filters.preferred_countries && filters.preferred_countries.length > 0) ||
        (filters.preferred_intents && filters.preferred_intents.length > 0) ||
        (filters.preferred_religions && filters.preferred_religions.length > 0)
      );
      setFiltersApplied(hasFilters);
      
      toast.success('Filters applied!');
      loadProfiles(); // Reload profiles with new filters
    } catch (error) {
      console.error('Failed to save filters:', error);
      toast.error('Failed to save filters');
    }
  };

  const remainingProfiles = profiles.slice(currentIndex);

  if (loading && profiles.length === 0) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 border-2 border-gold text-gold animate-spin mx-auto mb-4" />
          <p className="text-gray-400 font-body">Finding matches...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark" data-testid="discover-page">
      {/* Header */}
      <header className="sticky top-0 bg-dark/95 backdrop-blur-xl border-b border-white/10 z-40">
        <div className="flex items-center justify-between px-6 py-4">
          <h1 className="font-heading text-2xl text-gold-gradient">African Connections</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={loadProfiles}
              disabled={loading}
              className="p-2 text-gray-400 hover:text-white transition-colors disabled:opacity-50"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setShowFilters(true)}
              className={`p-2 transition-colors relative ${
                filtersApplied ? 'text-gold' : 'text-gray-400 hover:text-white'
              }`}
              data-testid="filters-btn"
            >
              <Sliders className="w-5 h-5" />
              {filtersApplied && (
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-gold rounded-full" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Card Stack */}
      <div className="relative px-4 pt-4 pb-32">
        <div className="relative h-[calc(100vh-200px)] max-h-[600px]">
          {remainingProfiles.length === 0 ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
              <div className="w-20 h-20 rounded-full bg-dark-elevated flex items-center justify-center mb-4">
                <RefreshCw className="w-10 h-10 text-gray-500" />
              </div>
              <h2 className="font-heading text-xl text-white mb-2">No More Profiles</h2>
              <p className="text-gray-400 font-body mb-6 max-w-xs">
                {filtersApplied 
                  ? 'Try adjusting your filters to see more people'
                  : 'Come back later to see more people'
                }
              </p>
              <div className="flex gap-3">
                {filtersApplied && (
                  <button
                    onClick={() => setShowFilters(true)}
                    className="btn-outline px-6 py-3 rounded-full font-body border border-gold/50 text-gold hover:bg-gold/10"
                  >
                    <Sliders className="w-4 h-4 mr-2 inline" />
                    Adjust Filters
                  </button>
                )}
                <button
                  onClick={loadProfiles}
                  className="btn-gold px-6 py-3 rounded-full font-body font-semibold"
                >
                  Refresh
                </button>
              </div>
            </div>
          ) : (
            <AnimatePresence>
              {remainingProfiles.slice(0, 3).map((profile, index) => (
                <SwipeCard
                  key={profile.user_id}
                  profile={profile}
                  onSwipe={handleSwipe}
                  isTop={index === 0}
                />
              ))}
            </AnimatePresence>
          )}
        </div>
      </div>

      {/* Match Modal */}
      <MatchModal
        isOpen={showMatch}
        onClose={() => setShowMatch(false)}
        matchedUser={matchData?.matched_user}
        matchId={matchData?.match_id}
      />

      {/* Filters Modal */}
      <DiscoveryFiltersModal
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        settings={settings}
        onSave={handleSaveFilters}
      />

      <BottomNav />
    </div>
  );
}
